import { useCanvasStore } from './canvas-store';
import type { CanvasImage } from './canvas-types';
import { queryClient } from '@/lib/queryClient';
import { triggerCreditsGate } from '@/lib/credits-gate';
import { toast } from '@/hooks/use-toast';

/**
 * Video-ad-clone client runner (Task 12) — modeled on canvas-clone-runner.ts.
 *
 * Two entry points:
 *   - runVideoClonePrepare: POST /api/video-clone/prepare (SSE). Streams the
 *     analyze → rewrite → storyboard phases. Paints THEIR original scene
 *     frames as row 0 plus one loading box per scene the moment vc_source
 *     arrives (v5.14 — seconds in, before the 30-60s rewrite), reconciles
 *     that paint against the script's scene set at vc_draft, and drops each
 *     branded storyboard frame one row beneath as it completes — aligned
 *     columns, one per script scene. The VideoScriptDoc is NEVER auto-opened
 *     (v5.14): the canvas is the run surface, the doc opens via a tile's
 *     hover "Scene details" button.
 *   - runVideoCloneGenerate: POST /api/video-clone/:draftId/generate (SSE).
 *     Streams per-scene clip generation + the final stitch, dropping playable
 *     tiles (poster src + adMeta.videoUrl) one row below their frames.
 *
 * SSE event names (vc_phase, vc_source, vc_draft, vc_frame_ready,
 * vc_frame_failed, vc_ready, vc_clip_progress, vc_clip_ready, vc_clip_failed,
 * vc_clips_done, vc_final_ready, vc_error) are a CONTRACT with
 * server/routes/videoClone.ts.
 * Do not rename.
 *
 * Both runs mirror progress into the project's chat via synthetic user +
 * assistant messages (the clone-runner pattern: `_writeMessages` keyed on a
 * stable per-run assistant message id).
 *
 * v5.13 — generation LIVES on the canvas: both runners drop loading
 * placeholders (addCanvasPlaceholder) at the exact slot each tile will land —
 * branded frames at vc_draft, clips at run start, the final at the stitch
 * phase — and resolve/fail them from the SSE events, so a video-clone run
 * reads like every other canvas run instead of a silent doc-side spinner.
 * Placeholder taskIds use run-scoped `vcprep-`/`vcgen-` prefixes: the store's
 * batch sweeps only ever target `agent-img-`/`intent-ph-` ids (scoped further
 * by batchId), so unrelated runs can never sweep a video-clone box.
 */

// ─── Layout constants (mirror canvas-store's private ones) ───────────────────
const START_X = 60;
const START_Y = 60;
const ROW_GAP = 48;
const GEN_GAP = 24;
const DEFAULT_WIDTH = 300;
const DEFAULT_HEIGHT = 300;

// Mirror of canvas-store's private isVirtualImage — seed/demo tiles must not
// count as "existing content" when computing the next free row.
function isVirtualImage(img: CanvasImage): boolean {
  return /^img-\d+$/.test(img.id) || img.id.startsWith('img-gen-demo-') || img.src.startsWith('data:');
}

/**
 * Next free row Y under all real content in the target project. Deliberately
 * ignores generationRows: frames placed with fixed positions never register a
 * generation row, so the store's row tracker can lag behind what is actually
 * on the canvas — the image bounding box is the ground truth here.
 */
function nextRowYUnderContent(projectId: number): number {
  const images = useCanvasStore.getState().projects[projectId]?.images ?? [];
  const real = images.filter((im) => !isVirtualImage(im));
  if (real.length === 0) return START_Y;
  const maxBottom = Math.max(...real.map((im) => im.y + im.displayHeight));
  return maxBottom + ROW_GAP;
}

// Same display-scale formula addImageToCanvas uses internally — needed up
// front to compute the x offsets of a row of same-size tiles.
function displayDims(width: number, height: number): { w: number; h: number } {
  const scale = Math.min(DEFAULT_WIDTH / width, DEFAULT_HEIGHT / height, 1);
  return { w: Math.round(width * scale), h: Math.round(height * scale) };
}

// Nominal pixel dims per script aspect — mirrors frameDims() in
// server/routes/videoClone.ts (the vc_clip_* events carry no dimensions).
function aspectDims(aspectRatio: string): [number, number] {
  if (aspectRatio === '9:16') return [720, 1280];
  if (aspectRatio === '16:9') return [1280, 720];
  return [1024, 1024];
}

/**
 * addImageToCanvas has a same-src choke point (v2.05): a second tile with an
 * identical src is silently skipped. Clip/final tiles reuse a frame's poster
 * URL as their src, so they'd be dropped without a disambiguating query param.
 * Bunny serves the same bytes regardless of unknown query params.
 */
function withTileTag(url: string, tag: string): string {
  return url + (url.includes('?') ? '&' : '?') + 'tile=' + tag;
}

/** v6.11 — per-generation tile tag derived from the VIDEO URL. MUST match the
 *  server's videoTileGenTag exactly: the server's persist-first recovery row
 *  and this client tile share one src so the (project_id, src) dedupe merges
 *  the real layout onto the raw (0,0) row. v6.04's Date.now() suffix broke
 *  that identity — refresh surfaced giant duplicate tiles at the origin. */
function videoTileGenTag(prefix: string, videoUrl: string): string {
  let h = 5381;
  for (let i = 0; i < videoUrl.length; i++) h = ((h * 33) ^ videoUrl.charCodeAt(i)) >>> 0;
  return `${prefix}-g${h.toString(36)}`;
}

// ─── Tile ↔ scene linkage (v5.13) ────────────────────────────────────────────

/**
 * Canvas tile src → the video-clone scene it belongs to. Key = the EXACT src
 * used at placement (including ?tile= disambiguation tags); sceneIndex null =
 * the final stitched tile. ImageNode reads this on hover to show the
 * "Scene details" button that opens the script doc at that scene.
 *
 * Populated two ways:
 *   - live, by the runners below, as each tile is placed;
 *   - on project load, by rebuildVideoCloneTileIndex (canvas-flow-workspace's
 *     project-load effect) so tiles restored from the DB stay linked.
 *
 * Entries are only ever added/overwritten, never cleared — srcs are
 * draft-scoped Bunny URLs, so stale entries from other projects are inert
 * (the tiles they describe are not on the active canvas).
 */
export const videoCloneTileIndex = new Map<string, { draftId: number; sceneIndex: number | null }>();

/**
 * Repopulate the tile index for one project from GET /api/video-clone
 * (?projectId=). The whole /api/video-clone router is admin-gated
 * (requireAdmin), so for non-admins this fetch 401/403s — swallowed silently,
 * which doubles as the client-side admin check (no hook available here).
 *
 * Src reconstruction MUST mirror the placement code exactly:
 *   originals      → withTileTag(originalFrameUrl, `original-${index}`)
 *   branded frames → startFrameUrl bare
 *   clips          → withTileTag(startFrameUrl, `clip-${index}`)  (poster src)
 *   final          → withTileTag(scenes[0].startFrameUrl, 'final')
 */
export async function rebuildVideoCloneTileIndex(projectId: number): Promise<void> {
  try {
    const res = await fetch(`/api/video-clone?projectId=${projectId}`);
    if (!res.ok) return; // 401/403 for non-admins; linkage is a nicety, never blocks load
    const drafts = await res.json();
    if (!Array.isArray(drafts)) return;
    for (const d of drafts) {
      const draftId = Number((d as Record<string, unknown>)?.id);
      const rawScenes = (d as Record<string, unknown>)?.scenes;
      if (!Number.isFinite(draftId) || !Array.isArray(rawScenes)) continue;
      for (const s of rawScenes as Array<Record<string, unknown>>) {
        const index = Number(s?.index);
        if (!Number.isFinite(index) || index < 0) continue;
        const startFrameUrl = typeof s?.startFrameUrl === 'string' ? s.startFrameUrl : '';
        const originalFrameUrl = typeof s?.originalFrameUrl === 'string' ? s.originalFrameUrl : '';
        if (startFrameUrl) {
          videoCloneTileIndex.set(startFrameUrl, { draftId, sceneIndex: index });
          videoCloneTileIndex.set(withTileTag(startFrameUrl, `clip-${index}`), { draftId, sceneIndex: index });
        }
        if (originalFrameUrl) {
          videoCloneTileIndex.set(withTileTag(originalFrameUrl, `original-${index}`), { draftId, sceneIndex: index });
        }
      }
      const first = (rawScenes as Array<Record<string, unknown>>).find((s) => Number(s?.index) === 0);
      const firstFrame = typeof first?.startFrameUrl === 'string' ? first.startFrameUrl : '';
      if (firstFrame) {
        videoCloneTileIndex.set(withTileTag(firstFrame, 'final'), { draftId, sceneIndex: null });
      }
    }
    // v6.04 — generation-suffixed tags (clip-0-g<ts> / final-g<ts>): exact
    // sets above can't enumerate them, so link any on-canvas video tile
    // whose BASE url matches a known frame. Tiles whose poster is no longer
    // a current script frame (older generations after a frame regen) stay
    // unlinked — playable history, no draft controls.
    const baseLinks = new Map<string, { draftId: number; sceneIndex: number | null }>();
    for (const [src, link] of Array.from(videoCloneTileIndex.entries())) {
      const q = src.indexOf('?');
      baseLinks.set(q === -1 ? src : src.slice(0, q), link);
    }
    const imgs = useCanvasStore.getState().projects[projectId]?.images ?? [];
    for (const im of imgs) {
      if (videoCloneTileIndex.has(im.src)) continue;
      if (!/[?&]tile=(clip-|final)/.test(im.src)) continue;
      const q = im.src.indexOf('?');
      const link = baseLinks.get(q === -1 ? im.src : im.src.slice(0, q));
      if (link) videoCloneTileIndex.set(im.src, link);
    }
    // v6.58 — RE-MATERIALIZE tiles for runs that outlived their client
    // (Dan: "it should run this stuff even if I click away and come back").
    // The server-side prepare survives a dropped SSE fine (emit swallows
    // dead-socket writes) and the draft finishes headless — but the CLIENT
    // is the only thing that places frame tiles, so returning showed just
    // the source video. Place whatever the drafts have that the canvas
    // lacks; if a draft is still preparing, poll it and materialize when
    // the frame lands.
    try {
      for (const d of drafts as Array<Record<string, unknown>>) {
        materializeCloneTiles(projectId, d);
      }
    } catch { /* placement is best-effort */ }
  } catch {
    /* network hiccup — the hover buttons just don't appear until next load */
  }
}

/** Place the opening-frame tile (and poll a still-preparing draft) when the
 *  canvas is missing what the draft already has. Idempotent by src. */
function materializeCloneTiles(projectId: number, d: Record<string, unknown>): void {
  const draftId = Number(d?.id);
  // The drafts LIST endpoint flattens scenes to the top level; the by-id
  // endpoint nests them under script. Accept both.
  const script = d?.script as { aspectRatio?: string; scenes?: Array<{ index?: number; startFrameUrl?: string | null }> } | undefined;
  const rawScenes = (Array.isArray(d?.scenes) ? d?.scenes : script?.scenes) as
    | Array<{ index?: number; startFrameUrl?: string | null }>
    | undefined;
  const aspect = (typeof d?.aspectRatio === "string" ? d.aspectRatio : script?.aspectRatio) ?? "9:16";
  if (!Number.isFinite(draftId) || !rawScenes?.length) return;
  const store = useCanvasStore.getState();
  if (store.activeProjectId !== projectId) return;
  const images = store.projects[projectId]?.images ?? [];
  const scenes = [...rawScenes].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  const s0 = scenes[0];
  const frameUrl = typeof s0?.startFrameUrl === "string" ? s0.startFrameUrl : null;

  const status = String(d?.status ?? "");
  if (!frameUrl) {
    // Still preparing headless — poll and materialize when the frame lands.
    if (status === "preparing") {
      let ticks = 0;
      const tick = async (): Promise<void> => {
        if (++ticks > 40) return;
        await new Promise((r) => setTimeout(r, 10_000));
        try {
          const res = await fetch(`/api/video-clone/${draftId}`);
          if (!res.ok) return;
          const fresh = await res.json();
          const st = useCanvasStore.getState();
          if (st.activeProjectId !== projectId) return; // navigated away again
          videoCloneTileIndex.set(String(fresh?.script?.scenes?.[0]?.startFrameUrl ?? ""), { draftId, sceneIndex: 0 });
          if (fresh?.script?.scenes?.[0]?.startFrameUrl) {
            materializeCloneTiles(projectId, fresh as Record<string, unknown>);
            return;
          }
          if (fresh?.status === "preparing") void tick();
        } catch { /* keep waiting */ }
      };
      void tick();
    }
    return;
  }
  // v6.90 — match the frame tile by its DRAFT-SCENE STEM, not exact src: the
  // edit/regen chain rewrites the filename (-eNNN/-rNNN suffixes), and any
  // missed repoint used to stack a NEW tile on top of the stale one (three
  // frames at one spot on draft 185 — "generating outside the bounding box"
  // came from slot lookups missing the drifted src).
  const isFrameTile = (im: { src: string; videoUrl?: string | null }) =>
    im.src.includes(`/draft-${draftId}-scene-`) && !im.videoUrl && !/[?&]tile=/.test(im.src);
  const existing = images.find((im) => im.src === frameUrl) ?? images.find(isFrameTile);
  if (existing) {
    if (existing.src !== frameUrl) {
      // Stale src (a repoint missed) — repoint in place instead of stacking
      // a duplicate; the client sync persists it.
      store.updateImage(existing.id, { src: frameUrl });
      videoCloneTileIndex.set(frameUrl, { draftId, sceneIndex: 0 });
      console.log(`[video-clone] repointed stale frame tile for draft ${draftId}`);
    }
    // Repair pass: a tile materialized before the drafts list carried
    // workflowRunId sits OUTSIDE the Clone Video Ad section box (membership
    // is clustered by run id). Stamp it so the box adopts it; the client
    // sync persists the field.
    const runId = Number(d?.workflowRunId);
    if (Number.isFinite(runId) && existing.workflowRunId == null) {
      store.updateImage(existing.id, { workflowRunId: runId });
      console.log(`[video-clone] repaired workflowRunId on frame tile for draft ${draftId}`);
    }
    void materializeCloneVideo(projectId, draftId, d, frameUrl, {
      x: existing.x, y: existing.y, w: existing.displayWidth, h: existing.displayHeight,
    });
    return;
  }

  // Anchor beside this draft's source-video tile when it exists; fresh row
  // under the content otherwise. Dims follow the source tile (same aspect).
  // Source tiles live OUTSIDE the tile index by design (v5.21), so match by
  // tag alone — a clone project has one source strip.
  const srcTile = images.find((im) => /[?&]tile=source-video/.test(im.src));
  const nom = aspect === "16:9" ? { w: 1280, h: 720 } : { w: 720, h: 1280 };
  const disp = srcTile
    ? { w: srcTile.displayWidth, h: srcTile.displayHeight }
    : displayDims(nom.w, nom.h);
  const x = srcTile ? srcTile.x + srcTile.displayWidth + GEN_GAP : START_X;
  const y = srcTile ? srcTile.y : nextRowYUnderContent(projectId);
  videoCloneTileIndex.set(frameUrl, { draftId, sceneIndex: 0 });
  console.log(`[video-clone] re-materialized frame tile for draft ${draftId} (run outlived its client)`);
  void store.addImageToCanvas(
    frameUrl, disp.w, disp.h, "branded-frame.png", nom.w, nom.h, undefined, x, y,
    { workflowRunId: (d?.workflowRunId as number) ?? undefined },
    projectId,
  );

  // Fall through to the video-side recovery below once the frame is placed.
  void materializeCloneVideo(projectId, draftId, d, frameUrl, { x, y, w: disp.w, h: disp.h });
}

/**
 * v7.14 — RIGHT EDGE of this draft's row. The clip loading box used to sit at
 * `firstFrame.x + width + GAP`, which WAS the end of the row back when a
 * draft had exactly one opening frame. Multi-window drafts (v6.96) put 2-3
 * frames on that row, so "right of the first frame" is now on top of frame 2
 * — Dan: "the generation box is placed in the middle over image, should have
 * appeared on the end next over". Anchor on the rightmost tile of the row
 * instead: this draft's frames (stem-matched, v6.90) plus any clip/final
 * videos already landed.
 */
function draftRowRightEdge(
  images: Array<{ x: number; y: number; displayWidth: number; src: string; videoUrl?: string | null }>,
  draftId: number,
  rowY: number,
  rowH: number,
): number | null {
  const band = rowH / 2;
  const mine = images.filter((im) => {
    if (Math.abs(im.y - rowY) >= band) return false;
    if (im.src.includes(`/draft-${draftId}-scene-`)) return true;           // frames
    if (im.videoUrl && videoCloneTileIndex.get(im.src)?.draftId === draftId) return true; // clips/final
    return false;
  });
  if (mine.length === 0) return null;
  return Math.max(...mine.map((im) => im.x + im.displayWidth));
}

/** Video-side recovery: a draft mid-GENERATION when its client left gets a
 *  loading box + a poll that lands the clip tile; a completed clip whose
 *  tile is missing lands immediately. Src mint MUST match the server's
 *  recovery row exactly (persist-first law). */
async function materializeCloneVideo(
  projectId: number,
  draftId: number,
  d: Record<string, unknown>,
  frameUrl: string,
  frameRect: { x: number; y: number; w: number; h: number },
): Promise<void> {
  const store = useCanvasStore.getState();
  if (store.activeProjectId !== projectId) return;
  const script = d?.script as { scenes?: Array<{ index?: number; clipUrl?: string | null }> } | undefined;
  const scenes = (Array.isArray(d?.scenes) ? d?.scenes : script?.scenes) as
    | Array<{ index?: number; clipUrl?: string | null }>
    | undefined;
  const first = scenes && [...scenes].sort((a, b) => (a.index ?? 0) - (b.index ?? 0))[0];
  const status = String(d?.status ?? "");

  const placeClip = (clipUrl: string): void => {
    const st = useCanvasStore.getState();
    if (st.activeProjectId !== projectId) return;
    const imgs = st.projects[projectId]?.images ?? [];
    const src = withTileTag(frameUrl, videoTileGenTag(`clip-${first?.index ?? 0}`, clipUrl));
    if (imgs.some((im) => im.src === src || im.videoUrl === clipUrl)) return; // already there (self-heal or live client)
    const band = frameRect.h / 2;
    const rowVideos = imgs.filter((im) => im.videoUrl && Math.abs(im.y - frameRect.y) < band);
    const x = Math.max(frameRect.x + frameRect.w, ...rowVideos.map((im) => im.x + im.displayWidth)) + GEN_GAP;
    videoCloneTileIndex.set(src, { draftId, sceneIndex: first?.index ?? 0 });
    console.log(`[video-clone] re-materialized video tile for draft ${draftId}`);
    void st.addImageToCanvas(
      src, frameRect.w, frameRect.h, "video-ad.mp4", 720, 1280, undefined, x, frameRect.y,
      { videoUrl: clipUrl, workflowRunId: (d?.workflowRunId as number) ?? undefined },
      projectId,
    );
  };

  if (typeof first?.clipUrl === "string" && first.clipUrl) {
    placeClip(first.clipUrl);
    return;
  }
  if (status !== "generating" && status !== "stitching") return;

  // Headless render in flight — loading box beside the frame + poll.
  const ph = `vcrec-${draftId}`;
  if (store.projects[projectId]?.canvasPlaceholders?.[ph]) return; // already watching
  {
    // v7.14 — end of the row, not right of this one frame (multi-window).
    const imgsNow = store.projects[projectId]?.images ?? [];
    const right = draftRowRightEdge(imgsNow, draftId, frameRect.y, frameRect.h) ?? (frameRect.x + frameRect.w);
    store.addCanvasPlaceholder(ph, right + GEN_GAP, frameRect.y, frameRect.w, frameRect.h, undefined, 720, 1280, "video-ad.mp4", projectId);
  }
  for (let i = 0; i < 60; i++) {
    await new Promise((r) => setTimeout(r, 10_000));
    const st = useCanvasStore.getState();
    if (st.activeProjectId !== projectId) { st.resolveCanvasPlaceholder(ph, projectId); return; }
    try {
      const res = await fetch(`/api/video-clone/${draftId}`);
      if (!res.ok) continue;
      const fresh = await res.json();
      const fs = (fresh?.script?.scenes ?? []) as Array<{ index?: number; clipUrl?: string | null }>;
      const f0 = [...fs].sort((a, b) => (a.index ?? 0) - (b.index ?? 0))[0];
      if (typeof f0?.clipUrl === "string" && f0.clipUrl) {
        st.resolveCanvasPlaceholder(ph, projectId);
        placeClip(f0.clipUrl);
        return;
      }
      if (fresh?.status === "failed") {
        st.failCanvasPlaceholder(ph, "video generation failed — open the script to retry", projectId);
        return;
      }
    } catch { /* keep polling */ }
  }
  useCanvasStore.getState().resolveCanvasPlaceholder(ph, projectId);
}

// ─── Chat frame edit (v5.33) ─────────────────────────────────────────────────

/** True when this canvas src is an editable video-clone START FRAME (not an
 *  original, clip poster, final, or the source-video tile). */
export function isVideoCloneFrameSrc(src: string): boolean {
  const entry = videoCloneTileIndex.get(src);
  if (!entry || entry.sceneIndex == null) return false;
  return !/[?&]tile=(original|clip|final|source)/.test(src);
}

/**
 * v5.33 — chat edit of a video-clone START FRAME. Dan typed "make the drink
 * colour blue" with a clone frame selected and the GENERIC variation agent
 * ran instead: square 1:1, restyled into a static ad, loose on the canvas,
 * draft untouched. This path: placeholder over the old tile → POST
 * edit-frame (surgical, frame-aspect, writes back to the draft scene) →
 * REPLACE the tile in place + retarget the tile index so Scene details and
 * Generate use the edited frame.
 */
export async function editVideoCloneFrameFromChat(imageId: string, instruction: string, onAccepted?: () => void): Promise<void> {
  const store = useCanvasStore.getState();
  const projectId = store.activeProjectId;
  if (projectId == null) return;
  const img = (store.projects[projectId]?.images ?? []).find((im) => im.id === imageId);
  if (!img) return;
  const entry = videoCloneTileIndex.get(img.src);
  if (!entry || entry.sceneIndex == null) return;
  const { draftId, sceneIndex } = entry;
  const { addImageToCanvas, addCanvasPlaceholder, resolveCanvasPlaceholder, failCanvasPlaceholder, deleteImage } = store;
  const ph = `vcedit-${draftId}-${sceneIndex}-${Date.now()}`;
  addCanvasPlaceholder(
    ph, img.x, img.y, img.displayWidth, img.displayHeight,
    undefined, img.originalWidth, img.originalHeight, `scene-${sceneIndex + 1}-edit.jpg`, projectId,
  );
  try {
    // Prior frame URL from the DRAFT (the tile src may carry ?tile= tags) —
    // the poll below judges success by this URL changing.
    let priorFrameUrl: string | null = null;
    try {
      const dres = await fetch(`/api/video-clone/${draftId}`);
      if (dres.ok) {
        const d = await dres.json();
        const sc = (d?.script?.scenes ?? []).find((s: { index?: number }) => Number(s?.index) === sceneIndex);
        priorFrameUrl = typeof sc?.startFrameUrl === 'string' ? sc.startFrameUrl : null;
      }
    } catch { /* poll still works via status */ }
    const res = await fetch(`/api/video-clone/${draftId}/scenes/${sceneIndex}/edit-frame`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instruction }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({} as Record<string, unknown>));
      if (res.status === 402) {
        const e = err as Record<string, unknown>;
        triggerCreditsGate(Number(e.balance ?? 0), Number(e.needed ?? 0));
      }
      throw new Error(String((err as Record<string, unknown>).error ?? `edit failed (${res.status})`));
    }
    onAccepted?.();
    // v6.08 — the route answers 202 (Cloudflare kills long requests at
    // ~100s; 4k edits run past it — Dan's "edit failed (524)" over a
    // successful edit). Poll the draft until it leaves "preparing", then
    // judge by the frame URL: changed = success.
    let newSrc = '';
    for (let i = 0; i < 150; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      let d: { status?: string; script?: { scenes?: Array<{ index?: number; startFrameUrl?: string | null }> } } | null = null;
      try {
        const pres = await fetch(`/api/video-clone/${draftId}`);
        if (!pres.ok) continue;
        d = await pres.json();
      } catch { continue; }
      if (d?.status === 'preparing') continue;
      const sc = (d?.script?.scenes ?? []).find((s) => Number(s?.index) === sceneIndex);
      const current = typeof sc?.startFrameUrl === 'string' ? sc.startFrameUrl : null;
      if (current && current !== priorFrameUrl) newSrc = current;
      break;
    }
    if (!newSrc) throw new Error('edit failed server-side — the frame is unchanged');
    // Replace in place: same slot, same run box (workflowRunId carries over).
    deleteImage(img.id);
    videoCloneTileIndex.delete(img.src);
    videoCloneTileIndex.set(newSrc, { draftId, sceneIndex });
    await addImageToCanvas(
      newSrc, img.originalWidth, img.originalHeight, `scene-${sceneIndex + 1}-frame.jpg`,
      img.originalWidth, img.originalHeight, `Scene ${sceneIndex + 1} — edited frame`,
      img.x, img.y, { workflowRunId: img.workflowRunId ?? undefined }, projectId,
    );
    resolveCanvasPlaceholder(ph, projectId);
    toast({ title: `Scene ${sceneIndex + 1} frame updated`, description: instruction.slice(0, 80) });
  } catch (e) {
    failCanvasPlaceholder(ph, e instanceof Error ? e.message : 'Frame edit failed', projectId);
    toast({ title: 'Frame edit failed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
  }
}

// ─── SSE plumbing ────────────────────────────────────────────────────────────

type SSEHandler = (data: Record<string, unknown>) => void | Promise<void>;

/**
 * POST → SSE loop (fetch → getReader → TextDecoder → "\n\n" blocks →
 * event/data regex → JSON.parse), same shape as canvas-clone-runner.
 * Pre-SSE failures (400/402/404/409/422) come back as plain JSON — those are
 * surfaced through `onRequestError` and the stream never starts. A 402 with
 * code CREDITS_EXHAUSTED additionally raises the shared out-of-credits modal.
 */
async function postSSE(
  url: string,
  body: Record<string, unknown>,
  handlers: Record<string, SSEHandler>,
  onRequestError: (message: string) => void,
): Promise<void> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok || !response.body) {
    let errBody: Record<string, unknown> | null = null;
    try { errBody = await response.json(); } catch { /* non-JSON error */ }
    if (response.status === 402 && errBody?.code === 'CREDITS_EXHAUSTED') {
      triggerCreditsGate(Number(errBody.balance ?? 0), Number(errBody.needed ?? 0));
      onRequestError('Not enough credits for this video run.');
    } else {
      const msg = errBody && typeof errBody.error === 'string' ? errBody.error : `Request failed (HTTP ${response.status})`;
      onRequestError(msg);
    }
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let sseBuffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    sseBuffer += decoder.decode(value, { stream: true });
    const blocks = sseBuffer.split('\n\n');
    sseBuffer = blocks.pop() ?? '';

    for (const block of blocks) {
      if (!block.trim()) continue;
      const eventMatch = block.match(/^event: (.+)$/m);
      const dataMatch = block.match(/^data: (.+)$/m);
      if (!eventMatch || !dataMatch) continue;
      const eventType = eventMatch[1].trim();
      let data: Record<string, unknown>;
      try { data = JSON.parse(dataMatch[1]); } catch { continue; }
      const handler = handlers[eventType];
      // Awaited on purpose: canvas placement handlers depend on earlier tiles
      // already being in the store (final row sits below the clip row).
      if (handler) await handler(data);
    }
  }
}

// ─── Chat mirror helpers ─────────────────────────────────────────────────────

interface ChatMirror {
  assistantMessageId: string;
  setLabel: (label: string) => void;
  /** Playbook-card mode: mark a phase running (completing earlier ones). */
  startPhase: (id: string, statusText?: string) => void;
  addWarning: (line: string) => void;
  addThumbnail: (url: string) => void;
  finish: (content: string) => void;
  fail: (content: string) => void;
}

// v5.61 (Dan: "we're missing the Playbook card animations for video") — the
// chat mirror now writes a real PlaybookTaskCard message (playbookProgress)
// like every other playbook run, with per-phase status animation. Plain-text
// mode remains for callers that pass no phases.
function createChatMirror(
  projectId: number,
  userContent: string,
  initialLabel: string,
  playbook?: { name: string; phases: Array<{ id: string; name: string }> },
): ChatMirror {
  const { _writeMessages, saveConversationHistory } = useCanvasStore.getState();
  const runId = `vclone-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const assistantMessageId = `msg-ai-${runId}`;
  const now = Date.now();
  let label = initialLabel;
  const warnings: string[] = [];

  const progress = playbook
    ? {
        playbookName: playbook.name,
        totalPhases: playbook.phases.length,
        phases: playbook.phases.map((p) => ({ id: p.id, name: p.name, phaseType: p.id })),
        phaseStatuses: Object.fromEntries(playbook.phases.map((p, i) => [p.id, i === 0 ? 'running' : 'pending'])) as Record<string, 'pending' | 'running' | 'completed' | 'failed'>,
        phaseStatusTexts: {} as Record<string, string>,
        activePhaseId: playbook.phases[0]?.id ?? null,
        qaResults: [] as never[],
        conceptProgress: null,
        completedSummary: null as string | null,
        isDone: false,
      }
    : null;

  _writeMessages(projectId, (msgs) => [
    ...msgs,
    { id: `msg-user-${runId}`, role: 'user' as const, content: userContent, timestamp: now },
    {
      id: assistantMessageId,
      role: 'assistant' as const,
      content: progress ? '' : initialLabel,
      isStreaming: true,
      thumbnails: [],
      timestamp: now + 1,
      ...(progress ? { playbookProgress: { ...progress, phaseStatuses: { ...progress.phaseStatuses }, phaseStatusTexts: { ...progress.phaseStatusTexts } } } : {}),
    },
  ]);
  saveConversationHistory(projectId).catch(() => {});

  const render = () => (progress ? warnings.join('\n') : [label, ...warnings].join('\n'));
  const patch = (p: { content?: string; isStreaming?: boolean; thumbnail?: string }) =>
    _writeMessages(projectId, (msgs) => msgs.map((m) =>
      m.id === assistantMessageId
        ? {
            ...m,
            ...(p.content !== undefined ? { content: p.content } : {}),
            ...(p.isStreaming !== undefined ? { isStreaming: p.isStreaming } : {}),
            ...(p.thumbnail ? { thumbnails: [...(m.thumbnails || []), p.thumbnail] } : {}),
            ...(progress ? { playbookProgress: { ...progress, phaseStatuses: { ...progress.phaseStatuses }, phaseStatusTexts: { ...progress.phaseStatusTexts } } } : {}),
          }
        : m));

  const startPhase = (id: string, statusText?: string) => {
    if (!progress) return;
    if (!progress.phases.some((p) => p.id === id)) return;
    for (const p of progress.phases) {
      if (p.id === id) break;
      if (progress.phaseStatuses[p.id] !== 'failed') progress.phaseStatuses[p.id] = 'completed';
    }
    progress.phaseStatuses[id] = 'running';
    progress.activePhaseId = id;
    if (statusText) progress.phaseStatusTexts[id] = statusText;
    patch({ content: render() });
  };

  const settle = (content: string, failed: boolean) => {
    label = content;
    if (progress) {
      for (const p of progress.phases) {
        if (progress.phaseStatuses[p.id] !== 'completed') {
          progress.phaseStatuses[p.id] = failed ? 'failed' : 'completed';
        }
      }
      progress.activePhaseId = null;
      progress.isDone = true;
      progress.completedSummary = content;
    }
    patch({ content: render(), isStreaming: false });
    saveConversationHistory(projectId).catch(() => {});
  };

  return {
    assistantMessageId,
    setLabel: (l) => {
      label = l;
      if (progress && progress.activePhaseId) progress.phaseStatusTexts[progress.activePhaseId] = l;
      patch({ content: render() });
    },
    startPhase,
    addWarning: (line) => { warnings.push(line); patch({ content: render() }); },
    addThumbnail: (url) => patch({ thumbnail: url }),
    finish: (content) => settle(content, false),
    // agent_error lesson: persist the failed state IMMEDIATELY — a user
    // navigating away right after an error must still see it after reload.
    fail: (content) => settle(content, true),
  };
}

// v5.61 — clickable "Video Script" card in chat (like other doc artifacts):
// opens the script panel for this draft.
function pushVideoScriptCard(projectId: number, draftId: number, title: string): void {
  const { _writeMessages, saveConversationHistory } = useCanvasStore.getState();
  _writeMessages(projectId, (msgs) => [
    ...msgs,
    {
      id: `msg-vscript-${draftId}-${Date.now()}`,
      role: 'assistant' as const,
      content: '',
      timestamp: Date.now(),
      videoScriptCard: { draftId, title },
    },
  ]);
  saveConversationHistory(projectId).catch(() => {});
}

// ─── CAPTIONS (v6.53) ────────────────────────────────────────────────────────
// TikTok-style dialog captions on a generated video tile. Picker (CapCut-
// style, brand colors) lives in CaptionStylePicker; this runs the burn:
// "Generating" placeholder beside the video → POST captions → poll → the
// captioned video lands as a NEW tile (never replaces — canvas doctrine).

export interface CaptionSwatch {
  id: string;
  name: string;
  swatchText: string;
  swatchStyle: Record<string, string>;
}

/** Swatches mirror server CAPTION_PRESETS (server validates ids). CSS here
 *  only approximates the burned look for the picker grid. */
export const CAPTION_PRESET_SWATCHES: CaptionSwatch[] = [
  { id: 'native', name: 'Native', swatchText: 'Aa', swatchStyle: { color: '#FFFFFF', WebkitTextStroke: '2px #000' } },
  { id: 'hormozi', name: 'Hormozi', swatchText: 'AA', swatchStyle: { color: '#FFE500', WebkitTextStroke: '2px #000' } },
  { id: 'karaoke', name: 'Karaoke', swatchText: 'Aa', swatchStyle: { color: '#58D700', WebkitTextStroke: '2px #000' } },
  { id: 'brandpink', name: 'Brand Pink', swatchText: 'Aa', swatchStyle: { color: '#9F2C53', WebkitTextStroke: '2px #000' } },
  { id: 'boxed', name: 'Boxed', swatchText: 'Aa', swatchStyle: { color: '#FFFFFF', background: '#141110', borderRadius: '6px', padding: '0 8px' } },
  { id: 'whisper', name: 'Whisper', swatchText: 'Aa', swatchStyle: { color: '#FFFFFF', WebkitTextStroke: '1px #000', opacity: '0.9' } },
];

export async function runVideoCaptions(imageId: string, preset: string): Promise<void> {
  const store = useCanvasStore.getState();
  const projectId = store.activeProjectId;
  if (projectId == null) return;
  const img = (store.projects[projectId]?.images ?? []).find((im) => im.id === imageId);
  if (!img || !img.videoUrl) return;
  const link = videoCloneTileIndex.get(img.src);
  if (!link) return;
  const { draftId } = link;
  const { updateImage, setCaptionBurning } = store;

  // v6.54 — IN-PLACE (Dan: "update the existing video, not a new one"): the
  // tile shows a "Generating captions" overlay, then its videoUrl swaps to
  // the new cut. Switching styles re-burns from the clean master server-side;
  // preset "off" restores the clean master instantly.
  setCaptionBurning(imageId, preset === 'off' ? 'Removing captions…' : 'Generating captions…');
  try {
    const res = await fetch(`/api/video-clone/${draftId}/captions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preset, videoUrl: img.videoUrl }),
    });
    if (res.status === 402) {
      const err = await res.json().catch(() => ({} as Record<string, unknown>));
      triggerCreditsGate(Number((err as Record<string, unknown>).balance ?? 0), Number((err as Record<string, unknown>).needed ?? 0));
      return;
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({} as Record<string, unknown>));
      throw new Error(String((err as Record<string, unknown>).error ?? `start failed (${res.status})`));
    }
    const { jobKey } = await res.json();

    let done: { status: string; url?: string; error?: string } = { status: 'running' };
    for (let i = 0; i < 100; i++) {
      try {
        const sres = await fetch(`/api/video-clone/${draftId}/captions/status?jobKey=${encodeURIComponent(jobKey)}`);
        if (sres.ok) {
          done = await sres.json();
          if (done.status === 'done' || done.status === 'failed') break;
        }
      } catch { /* keep polling */ }
      await new Promise((r) => setTimeout(r, 3000));
    }
    if (done.status !== 'done' || !done.url) throw new Error(done.error || 'caption burn timed out');

    // Swap the tile's video in place (server already repointed the DB rows —
    // the same URL swap, so client and DB agree). Cache-bust nothing: the
    // captioned cut is a distinct URL.
    updateImage(imageId, { videoUrl: done.url });
  } catch (e) {
    console.warn('[captions]', e instanceof Error ? e.message : e);
  } finally {
    setCaptionBurning(imageId, null);
  }
}

// ─── CHAT EDIT (v6.05) ───────────────────────────────────────────────────────

function pushVideoGenerateCard(projectId: number, draftId: number, title: string): void {
  const { _writeMessages, saveConversationHistory } = useCanvasStore.getState();
  _writeMessages(projectId, (msgs) => [
    ...msgs,
    {
      id: `msg-vgen-${draftId}-${Date.now()}`,
      role: 'assistant' as const,
      content: '',
      timestamp: Date.now(),
      videoGenerateCard: { draftId, title },
    },
  ]);
  saveConversationHistory(projectId).catch(() => {});
}

// 30s TTL cache: is there a chat-editable clone draft in this project?
const draftLookupCache = new Map<number, { at: number; draftId: number | null; busy: boolean }>();

/** Latest READY clone draft in the project (draftId), plus whether a draft is
 *  currently BUSY (preparing/generating/stitching — v6.06: a busy draft must
 *  get an honest "still running" chat reply, never the generic agent, which
 *  answered "I don't have video tools" to Dan's edit). Cached 30s. */
export async function latestEditableDraftId(
  projectId: number,
): Promise<{ draftId: number | null; busy: boolean }> {
  const hit = draftLookupCache.get(projectId);
  if (hit && Date.now() - hit.at < 30_000) return { draftId: hit.draftId, busy: hit.busy };
  let draftId: number | null = null;
  let busy = false;
  try {
    const res = await fetch(`/api/video-clone?projectId=${projectId}`);
    if (res.ok) {
      const drafts = await res.json();
      if (Array.isArray(drafts)) {
        // Edits target the NEWEST draft only — routing to an older ready
        // draft while the newest generates would edit the wrong video.
        const newest = drafts
          .filter((d: Record<string, unknown>) => Number.isFinite(Number(d?.id)))
          .sort((a: Record<string, unknown>, b: Record<string, unknown>) => Number(b.id) - Number(a.id))[0];
        if (newest) {
          if (newest.status === 'ready') draftId = Number(newest.id);
          else if (newest.status === 'preparing' || newest.status === 'generating' || newest.status === 'stitching') busy = true;
        }
      }
    }
  } catch { /* network hiccup — treat as no draft */ }
  draftLookupCache.set(projectId, { at: Date.now(), draftId, busy });
  return { draftId, busy };
}

/** v6.06 — honest busy reply (echoes the user message, then explains). */
export function pushVideoBusyNotice(projectId: number, userText: string): void {
  const chat = createChatMirror(projectId, userText, 'Checking your video…');
  chat.finish(
    "Your video is still generating — I can't edit the script mid-run without losing your change. Send this again as soon as the video finishes and I'll apply it.",
  );
}

/**
 * v6.05 — plain-language edit of the WHOLE video from chat (Dan: "make the
 * blue drink more transparent blue" → adjust every prompt that needs it,
 * explain it, then give me a button that makes a new video). One server call
 * classifies + rewrites the script; a returned frameInstruction re-edits the
 * opening frame through the existing edit-frame flow (tile replaced in
 * place). Returns 'fallthrough' when the message isn't a video edit — the
 * caller sends it to the normal agent instead.
 */
export async function runVideoCloneChatEdit(
  projectId: number,
  draftId: number,
  instruction: string,
): Promise<'handled' | 'handled-frame' | 'fallthrough'> {
  let resp: Response;
  try {
    resp = await fetch(`/api/video-clone/${draftId}/chat-edit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instruction }),
    });
  } catch {
    return 'fallthrough';
  }
  if (!resp.ok) {
    if (resp.status === 402) {
      const err = await resp.json().catch(() => ({} as Record<string, unknown>));
      triggerCreditsGate(Number((err as Record<string, unknown>).balance ?? 0), Number((err as Record<string, unknown>).needed ?? 0));
      return 'handled';
    }
    // v6.06 — a BUSY draft gets an honest reply, never the generic agent
    // (it answered "I don't have video tools" to a video edit).
    if (resp.status === 409) {
      pushVideoBusyNotice(projectId, instruction);
      return 'handled';
    }
    return 'fallthrough'; // 422 stale script → let the agent answer
  }
  const data = await resp.json().catch(() => null) as {
    applies?: boolean; explanation?: string; changes?: string[]; frameInstruction?: string | null;
  } | null;
  if (!data || data.applies !== true) return 'fallthrough';

  // The edit is applied server-side already — mirror the flow into chat.
  draftLookupCache.delete(projectId);
  const chat = createChatMirror(projectId, instruction, 'Updating your video…', {
    name: 'Edit Video',
    phases: [
      { id: 'script', name: 'Rewrite the affected prompts' },
      ...(data.frameInstruction ? [{ id: 'frame', name: 'Update the opening frame' }] : []),
    ],
  });
  chat.startPhase('script', 'Script updated');
  for (const line of (data.changes ?? []).slice(0, 8)) chat.addWarning(line);

  if (data.frameInstruction) {
    chat.startPhase('frame', 'Re-editing the opening frame…');
    try {
      // Prefer the full tile-replacing flow when the frame tile is on canvas.
      const store = useCanvasStore.getState();
      const images = store.projects[projectId]?.images ?? [];
      const frameTile = images.find((im) => {
        const link = videoCloneTileIndex.get(im.src);
        return link?.draftId === draftId && link.sceneIndex === 0 && !im.videoUrl && isVideoCloneFrameSrc(im.src);
      });
      if (frameTile) {
        await editVideoCloneFrameFromChat(frameTile.id, data.frameInstruction);
      } else {
        const r = await fetch(`/api/video-clone/${draftId}/scenes/0/edit-frame`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ instruction: data.frameInstruction }),
        });
        if (!r.ok) throw new Error(`frame edit failed (${r.status})`);
        // v6.08 — 202-async: wait for the draft to leave "preparing".
        for (let i = 0; i < 150; i++) {
          await new Promise((res) => setTimeout(res, 4000));
          try {
            const pres = await fetch(`/api/video-clone/${draftId}`);
            if (!pres.ok) continue;
            const d = await pres.json();
            if (d?.status !== 'preparing') break;
          } catch { /* keep polling */ }
        }
      }
    } catch (err) {
      chat.addWarning(`Opening-frame update failed (${err instanceof Error ? err.message : 'error'}) — the script changes are saved; regenerate the frame from its tile if the old look persists.`);
    }
  }

  chat.finish(`${data.explanation || 'Done — your changes are in the script.'} Hit the button below to generate the new video.`);
  pushVideoGenerateCard(projectId, draftId, 'Generate the new video');
  // 'handled-frame' tells element-scoped callers the opening frame actually
  // changed (crops are stale, re-detection warranted). Script-only edits
  // return plain 'handled' so the caller does NOT re-detect — re-detection
  // would overwrite the correction just merged into the element ground truth
  // with a fresh vision paraphrase.
  return data.frameInstruction ? 'handled-frame' : 'handled';
}

// ─── PREPARE ─────────────────────────────────────────────────────────────────

export async function runVideoClonePrepare(args: { templateId: number; brandId: number; projectId: number; productId?: number }): Promise<void> {
  const { templateId, brandId, projectId, productId } = args;
  const { addImageToCanvas, addCanvasPlaceholder, resolveCanvasPlaceholder, failCanvasPlaceholder } = useCanvasStore.getState();
  const chat = createChatMirror(projectId, 'Clone this video ad', 'Starting video clone…', {
    name: 'Clone Video Ad',
    phases: [
      { id: 'analysis', name: 'Analyze the source video' },
      { id: 'script', name: 'Rewrite the script for your brand' },
      { id: 'opening_frame', name: 'Opening frame + QA' },
    ],
  });

  // Storyboard rows (v5.10 original-vs-branded): row 0 = THEIR original scene
  // frames, row 1 = YOUR branded start-frames directly beneath, same column x
  // per sceneIndex. Column i sits at START_X + i * (displayW + GEN_GAP) — the
  // clone-runner's row math with the row origin computed from the canvas
  // bounding box instead of a source tile.
  const originalRowY = nextRowYUnderContent(projectId);
  // Branded frames land on the original row's Y until originals actually
  // arrive (pre-redesign drafts / extraction miss) — then shift one row down.
  let brandedRowY = originalRowY;
  let frameDisp: { w: number; h: number } | null = null;
  let sawTerminal = false;
  // v5.58 — which scenes get a branded frame (server-announced; null = all).
  let brandedFrameScenes: number[] | null = null;
  // v7.00 — where each window-first frame's loading box actually sits, so
  // vc_frame_ready lands the tile in ITS OWN column (the v5.61 hardcoded
  // column-1 stacked multi-window frames onto each other and the collision
  // guard nudged them over neighbors — Dan: "images stacked on top of
  // eachother").
  const frameSlots = new Map<number, { x: number; y: number }>();

  // v5.13 — one loading box per scene on the branded row, resolved/failed per
  // vc_frame_* event. Run-scoped taskId prefix; no prompt on purpose — the
  // legacy retryPlaceholder path re-fires prompts into the IMAGE pipeline,
  // which must never happen for video-clone boxes.
  // v5.14 — the storyboard paints at vc_source (fires within ~1s) and vc_draft
  // reconciles against the SCRIPT's scene set.
  // v5.15 — the server pre-merges scenes deterministically BEFORE vc_source
  // and pins the rewrite to that exact set, so the sets always match and the
  // reconcile below is a pure safety net (kept as the belt in case a future
  // server change ever lets them diverge again — sweep-and-replace).
  const phRun = `vcprep-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const framePh = (sceneIndex: number) => `${phRun}-frame-${sceneIndex}`;
  const outstanding = new Set<string>();
  const failOutstanding = (msg: string) => {
    // .forEach — the repo's TS target can't for..of a Set (TS2802).
    outstanding.forEach((tid) => failCanvasPlaceholder(tid, msg, projectId));
    outstanding.clear();
  };
  let runDraftId: number | null = null;
  // v5.17 — the run id (vc_source/vc_draft carry it) stamps every placed tile
  // (adMeta.workflowRunId) so the whole clone gets one "Clone Video Ad"
  // section frame on the canvas (PLAYBOOK_FRAME_LABELS['video-clone']).
  let runWorkflowRunId: number | null = null;
  const runMeta = () => (runWorkflowRunId != null ? { workflowRunId: runWorkflowRunId } : undefined);

  // What the storyboard paint has placed so far — the reconcile bookkeeping.
  let storyboardPlaced = false;
  let placedIndexes: number[] = [];
  let placedOriginals: Array<{ src: string; sceneIndex: number }> = [];

  /**
   * Paint the storyboard for one scene set: originals row (scenes that have a
   * frame) + one branded-row loading box per scene. Shared by vc_source
   * (analysis scenes) and the vc_draft reconcile path (script scenes).
   */
  const placeStoryboard = async (
    scenes: Array<{ sceneIndex: number; originalFrameUrl: string }>,
    aspect: string,
  ): Promise<void> => {
    const [nomW, nomH] = aspectDims(aspect);
    if (!frameDisp) frameDisp = displayDims(nomW, nomH);
    // v5.61 (Dan: "3 pieces in a row") — ONE ROW: [source video] [branded
    // opening frame] [video] [final]. No original-frame stills on the canvas
    // (they live in the script doc as the per-scene "Theirs" references).
    brandedRowY = originalRowY;
    // Loading boxes for the opening frame(s) at COLUMN 1+ (right of the
    // source video). v6.96 — multi-window sources carry ONE branded frame
    // PER GENERATION WINDOW (brandedFrameScenes lists each window-first
    // scene); legacy runs without the hint still get exactly the first
    // scene's.
    const brandedScenes = brandedFrameScenes && brandedFrameScenes.length > 0
      ? brandedFrameScenes
      : [scenes[0]?.sceneIndex ?? 0];
    let brandedCol = 0;
    for (const s of scenes) {
      if (!brandedScenes.includes(s.sceneIndex)) continue;
      const slotX = START_X + (frameDisp.w + GEN_GAP) * (1 + brandedCol);
      frameSlots.set(s.sceneIndex, { x: slotX, y: brandedRowY });
      addCanvasPlaceholder(
        framePh(s.sceneIndex), slotX, brandedRowY, frameDisp.w, frameDisp.h,
        undefined, nomW, nomH, brandedScenes.length > 1 ? `opening-frame-part-${brandedCol + 1}.jpg` : 'opening-frame.jpg', projectId,
      );
      outstanding.add(framePh(s.sceneIndex));
      brandedCol++;
    }
    placedIndexes = scenes.map((s) => s.sceneIndex);
    storyboardPlaced = true;
  };

  /** Undo a storyboard paint: delete the original tiles (store + DB — the
   *  sweep is active-project scoped like every deleteImages call; if the user
   *  switched projects mid-run the rare merged-scene re-place leaves the old
   *  originals behind, which is cosmetic) and clear the loading boxes. */
  const sweepStoryboard = (): void => {
    const { deleteImage, projects } = useCanvasStore.getState();
    const images = projects[projectId]?.images ?? [];
    for (const placed of placedOriginals) {
      const tile = images.find((im) => im.src === placed.src);
      if (tile) deleteImage(tile.id);
      videoCloneTileIndex.delete(placed.src);
    }
    placedOriginals = [];
    outstanding.forEach((tid) => resolveCanvasPlaceholder(tid, projectId));
    outstanding.clear();
    placedIndexes = [];
    frameSlots.clear();
    storyboardPlaced = false;
  };

  try {
    await postSSE(
      '/api/video-clone/prepare',
      // v5.40 — productId pins multi-product brands to the user's pick.
      // v7.16 — imageModel: the composer dropdown's choice for THIS task;
      // prepare stamps it onto script.imageModel so every frame/card op of
      // this draft renders with one family.
      {
        templateId,
        brandId,
        projectId,
        ...(productId != null ? { productId } : {}),
        imageModel: useCanvasStore.getState().imageModelByProject[projectId]
          ?? useCanvasStore.getState().imageModel,
      },
      {
        vc_phase: (d) => {
          const phase = String(d.phase ?? '');
          const label = String(d.label ?? d.phase ?? 'Working…');
          // v5.61 — playbook-card phases: analysis → script → opening frame.
          if (phase === 'script') chat.startPhase('script', label);
          else if (phase === 'storyboard') chat.startPhase('opening_frame', label);
          else chat.setLabel(label);
        },
        // v5.14 — instant canvas: the source storyboard arrives seconds in
        // (before the 30-60s rewrite), so the originals row + loading boxes
        // paint immediately. Indexes are ANALYSIS-scene indexes; vc_draft
        // reconciles them against the script below.
        vc_source: async (d) => {
          const rid = Number(d.workflowRunId);
          if (Number.isFinite(rid) && rid > 0) runWorkflowRunId = rid;
          if (storyboardPlaced) return; // single emission expected — defensive
          const rawScenes = Array.isArray(d.scenes) ? (d.scenes as unknown[]) : [];
          const scenes = rawScenes
            .map((s) => {
              const rec = (s ?? {}) as Record<string, unknown>;
              return {
                sceneIndex: Number(rec.sceneIndex ?? NaN),
                originalFrameUrl: typeof rec.originalFrameUrl === 'string' ? rec.originalFrameUrl : '',
              };
            })
            .filter((s) => Number.isFinite(s.sceneIndex) && s.sceneIndex >= 0);
          if (scenes.length === 0) return;
          if (Array.isArray(d.brandedFrameScenes)) {
            brandedFrameScenes = (d.brandedFrameScenes as unknown[])
              .map(Number)
              .filter((n) => Number.isFinite(n));
          }
          const aspect = typeof d.aspectRatio === 'string' ? d.aspectRatio : '9:16';
          await placeStoryboard(scenes, aspect);
          // v5.21 — the SOURCE AD as a hover-playing video tile at the head
          // of the originals row (column -1, so scene columns keep their
          // sceneIndex-based x). Poster = first placed original frame.
          // Deliberately NOT in placedOriginals (reconcile sweeps must not
          // delete it) and NOT in videoCloneTileIndex (it has no scene).
          const sourceVideoUrl = typeof d.sourceVideoUrl === 'string' ? d.sourceVideoUrl : '';
          const poster = scenes.find((s) => s.originalFrameUrl !== '')?.originalFrameUrl ?? '';
          if (sourceVideoUrl && poster && frameDisp) {
            const [nomW, nomH] = aspectDims(aspect);
            // v5.61 — column 0 of the single row (frame lands at column 1,
            // video at column 2, final at column 3).
            await addImageToCanvas(
              withTileTag(poster, 'source-video'), nomW, nomH, 'source-ad.jpg',
              nomW, nomH, 'Original source video ad',
              START_X, originalRowY,
              { videoUrl: sourceVideoUrl, conceptName: 'Original source ad', workflowRunId: runWorkflowRunId ?? undefined },
              projectId,
            );
          }
        },
        vc_draft: async (d) => {
          const draftId = Number(d.draftId);
          if (Number.isFinite(draftId)) runDraftId = draftId;
          const rid = Number(d.workflowRunId);
          if (Number.isFinite(rid) && rid > 0) runWorkflowRunId = rid;
          // Draft snapshot: the SCRIPT scene set (indexes + server-stamped
          // originalFrameUrl) + stored aspect — the reconcile ground truth.
          // The draft row is stored WITH its full script before vc_draft is
          // emitted.
          let aspect: string | null = null;
          let scriptScenes: Array<{ sceneIndex: number; originalFrameUrl: string }> | null = null;
          if (Number.isFinite(draftId)) {
            try {
              const res = await fetch(`/api/video-clone/${draftId}`);
              if (res.ok) {
                const draft = await res.json();
                aspect = draft?.script?.aspectRatio ?? draft?.aspectRatio ?? null;
                const scenes = draft?.script?.scenes;
                if (Array.isArray(scenes)) {
                  scriptScenes = scenes
                    .map((s: Record<string, unknown>) => ({
                      sceneIndex: Number(s?.index),
                      originalFrameUrl: typeof s?.originalFrameUrl === 'string' ? s.originalFrameUrl : '',
                    }))
                    .filter((s: { sceneIndex: number }) => Number.isFinite(s.sceneIndex) && s.sceneIndex >= 0);
                }
              }
            } catch { /* payload fallback below */ }
          }
          // Snapshot-fetch fallback: the payload's originalFrames is the same
          // per-SCRIPT-scene mapping (scenes without frames absent — still
          // good enough to reconcile columns against).
          if (!scriptScenes || scriptScenes.length === 0) {
            const rawFrames = Array.isArray(d.originalFrames) ? (d.originalFrames as unknown[]) : [];
            const fromPayload = rawFrames
              .map((f) => {
                const rec = (f ?? {}) as Record<string, unknown>;
                return {
                  sceneIndex: Number(rec.sceneIndex ?? NaN),
                  originalFrameUrl: typeof rec.url === 'string' ? rec.url : '',
                };
              })
              .filter((f) => Number.isFinite(f.sceneIndex) && f.sceneIndex >= 0 && f.originalFrameUrl !== '');
            if (fromPayload.length > 0) scriptScenes = fromPayload;
          }
          // The draftId exists now — link whatever originals are already up.
          if (runDraftId != null) {
            for (const p of placedOriginals) {
              videoCloneTileIndex.set(p.src, { draftId: runDraftId, sceneIndex: p.sceneIndex });
            }
          }
          // Nothing to reconcile against (fetch failed AND frameless payload)
          // — keep vc_source's paint; vc_ready sweeps any index-mismatched
          // leftover boxes.
          if (!scriptScenes || scriptScenes.length === 0) return;
          // Common case: the rewrite kept the analysis scene set 1:1 — the
          // instant paint already stands, nothing to do.
          const scriptIndexes = scriptScenes.map((s) => s.sceneIndex).sort((a, b) => a - b);
          const sameSet =
            storyboardPlaced &&
            placedIndexes.length === scriptIndexes.length &&
            placedIndexes.slice().sort((a, b) => a - b).join(',') === scriptIndexes.join(',');
          if (sameSet) return;
          // Scene sets differ (the rewrite merged scenes) or vc_source never
          // painted — sweep and re-place per SCRIPT scene. A brief re-place
          // flicker is acceptable here.
          sweepStoryboard();
          await placeStoryboard(scriptScenes, aspect ?? '9:16');
        },
        vc_frame_ready: async (d) => {
          const sceneIndex = Number(d.sceneIndex ?? 0);
          const url = String(d.url ?? '');
          if (!url) return;
          const tid = framePh(sceneIndex);
          if (outstanding.has(tid)) {
            resolveCanvasPlaceholder(tid, projectId);
            outstanding.delete(tid);
          }
          const width = Number(d.width) || 720;
          const height = Number(d.height) || 1280;
          if (!frameDisp) frameDisp = displayDims(width, height);
          // v7.00 — each frame lands in ITS OWN loading box's slot (v5.61's
          // hardcoded column 1 stacked multi-window frames onto each other);
          // legacy single-frame runs keep column 1 via the fallback.
          const slot = frameSlots.get(sceneIndex);
          const frameX = slot?.x ?? START_X + (frameDisp.w + GEN_GAP);
          const frameY = slot?.y ?? brandedRowY;
          if (runDraftId != null) videoCloneTileIndex.set(url, { draftId: runDraftId, sceneIndex });
          const partN = brandedFrameScenes && brandedFrameScenes.length > 1
            ? brandedFrameScenes.slice().sort((a, b) => a - b).indexOf(sceneIndex) + 1
            : 0;
          await addImageToCanvas(
            url, width, height, partN > 0 ? `opening-frame-part-${partN}.jpg` : 'opening-frame.jpg',
            width, height, partN > 0 ? `Opening frame — part ${partN}` : 'Opening frame — anchors the whole video',
            frameX, frameY, runMeta(), projectId,
          );
          chat.addThumbnail(url);
        },
        vc_frame_failed: (d) => {
          const sceneIndex = Number(d.sceneIndex ?? 0);
          const tid = framePh(sceneIndex);
          if (outstanding.has(tid)) {
            failCanvasPlaceholder(tid, 'Frame failed — regenerate it from the script doc.', projectId);
            outstanding.delete(tid);
          }
          // v5.15 — show the REAL server error (moderation vs transient vs
          // timeout reads completely differently to the user); it was being
          // dropped here, which made Dan's draft-34 failure undiagnosable
          // from the chat.
          const detail = typeof d.error === 'string' && d.error ? ` (${d.error})` : '';
          chat.addWarning(`Frame for scene ${sceneIndex + 1} failed${detail} — regenerate it from the scene details panel.`);
        },
        vc_ready: (d) => {
          sawTerminal = true;
          // Every scene emitted ready or failed before vc_ready; a leftover
          // box means its event was dropped mid-stream (or vc_source painted
          // analysis indexes the reconcile couldn't map) — the frame may well
          // exist (the doc shows the truth), so clear rather than fail it.
          outstanding.forEach((tid) => resolveCanvasPlaceholder(tid, projectId));
          outstanding.clear();
          const draftId = Number(d.draftId);
          if (Number.isFinite(draftId)) {
            queryClient.invalidateQueries({ queryKey: ['/api/video-clone', draftId] });
          }
          // v5.14 — no doc auto-open: the canvas is the run surface; the doc
          // opens only via a tile's hover "Scene details" button (or the
          // templates-view flows).
          // v5.58 — explain the single-pass model up front (Dan's first run:
          // "it's only making 1 image even though it split things into more?"
          // — the one branded frame is @Image 1, not a missing-frames bug).
          chat.finish('Script ready — the opening frame anchors the whole video, and Generate creates every scene in a single pass (cuts, voiceover and text included).');
          // v5.61 — clickable script card in chat (opens the script doc).
          if (runDraftId != null) pushVideoScriptCard(projectId, runDraftId, 'Video Script — review & edit before generating');
        },
        vc_error: (d) => {
          sawTerminal = true;
          failOutstanding('Video clone failed');
          chat.fail(`Video clone failed: ${String(d.error ?? 'unknown error')}`);
        },
      },
      (message) => { sawTerminal = true; failOutstanding(message); chat.fail(message); },
    );
    if (!sawTerminal) {
      failOutstanding('Connection lost mid-run');
      chat.fail('Connection lost mid-run — reopen the script doc to check where it got to.');
    }
  } catch (err) {
    console.error('[VideoCloneRunner] prepare error:', err);
    failOutstanding('Video clone failed');
    if (!sawTerminal) chat.fail('Video clone failed — see console for details.');
    throw err;
  }
}

/**
 * Shared "the picker handed back a projectId" step for template-driven video
 * clones: flip into the chat-canvas split view, resolve the destination
 * project's brand (the prepare route requires brandId), then fire the SSE
 * prepare runner. Used by canvas-ad-library-view.tsx (the live Ad library →
 * Ad templates tab). canvas-ad-templates-view.tsx carries a local equivalent
 * (`handleVideoClonePicked`) — it's the E2E surface and is intentionally left
 * as-is; fold it into this helper next time that file is touched.
 */
export async function pickProjectAndPrepareVideoClone(args: { templateId: number; projectId: number; productId?: number }): Promise<void> {
  const { templateId, projectId, productId } = args;
  const { setActiveView, setCanvasOpen } = useCanvasStore.getState();
  setActiveView('chat-canvas');
  setCanvasOpen(true);
  let brandId: number | null = null;
  try {
    const resp = await fetch(`/api/canvas/projects/${projectId}`);
    if (resp.ok) {
      const proj = await resp.json();
      brandId = proj.brandId ?? proj.brand_id ?? null;
    }
  } catch (err) {
    console.warn('[VideoClone] failed to resolve brand for project', projectId, err);
  }
  if (!brandId) {
    toast({
      title: 'No brand on this task',
      description: 'Video clone needs a brand — pick a task that belongs to a brand.',
      variant: 'destructive',
    });
    return;
  }
  useCanvasStore.setState({ activeBrandId: brandId });
  await runVideoClonePrepare({ templateId, brandId, projectId, productId }).catch((err) => {
    console.error('[VideoClone] prepare failed:', err);
  });
}

/**
 * v5.40 — product resolution for the clone flow (Dan: the script name-dropped
 * a SECOND product the ad never shows). Resolves the destination project's
 * brand and lists its products; the caller shows a picker when there are 2+
 * (single-product brands skip straight to prepare, like every other flow).
 */
// v7.53 — retried: this decides whether the product question is asked at
// all, and it used to fail-open to [] on the FIRST non-OK fetch — one
// transient 500 (cold DB pool, a deploy) silently skipped the ask and the
// clone ran on the first product.
async function fetchOkWithRetry(url: string, tries = 3): Promise<Response | null> {
  for (let i = 0; i < tries; i++) {
    try {
      const r = await fetch(url);
      if (r.ok) return r;
    } catch {}
    await new Promise((res) => setTimeout(res, 800 * (i + 1)));
  }
  return null;
}

export async function listBrandProductsForProject(
  projectId: number,
): Promise<Array<{ id: number; name: string }>> {
  try {
    const resp = await fetchOkWithRetry(`/api/canvas/projects/${projectId}`);
    if (!resp) return [];
    const proj = await resp.json();
    const brandId = proj.brandId ?? proj.brand_id ?? null;
    if (!brandId) return [];
    const res = await fetchOkWithRetry(`/api/brands/${brandId}/products`);
    if (!res) return [];
    const products = await res.json();
    if (!Array.isArray(products)) return [];
    return products
      .map((p: Record<string, unknown>) => ({ id: Number(p?.id), name: String(p?.name ?? '') }))
      .filter((p) => Number.isFinite(p.id) && p.name.length > 0);
  } catch {
    return [];
  }
}

// ─── GENERATE ────────────────────────────────────────────────────────────────

export async function runVideoCloneGenerate(draftId: number, projectId: number): Promise<void> {
  const { addImageToCanvas, addCanvasPlaceholder, resolveCanvasPlaceholder, failCanvasPlaceholder } = useCanvasStore.getState();

  // Draft snapshot up front: scene count for progress copy, aspect ratio for
  // tile dims (the vc_clip_* events carry no dimensions), and the PENDING
  // scene set (clipStatus !== 'done' — the server's own re-generate filter)
  // for the clips-row loading boxes.
  let sceneCount = 0;
  let aspect = '9:16';
  let pendingScenes: Array<{ index: number; startFrameUrl: string | null }> = [];
  // v5.61 — full scene set too: the single-row slot math needs SOME frame
  // tile even on stitch-only resumes (where pendingScenes is empty).
  let draftScenes: Array<{ index: number; startFrameUrl: string | null }> = [];
  // v5.17 — the draft row carries the prepare run's id; stamping it on clip/
  // final tiles keeps them inside the "Clone Video Ad" section frame (the
  // server's persist-first rows already stamp it — this covers the in-memory
  // tiles the runner places before any reload).
  let runWorkflowRunId: number | null = null;
  try {
    const res = await fetch(`/api/video-clone/${draftId}`);
    if (res.ok) {
      const draft = await res.json();
      sceneCount = draft?.script?.scenes?.length ?? 0;
      aspect = draft?.script?.aspectRatio ?? draft?.aspectRatio ?? '9:16';
      const rid = Number(draft?.workflowRunId);
      if (Number.isFinite(rid) && rid > 0) runWorkflowRunId = rid;
      const scenes = draft?.script?.scenes;
      if (Array.isArray(scenes)) {
        const mapScene = (s: Record<string, unknown>) => ({
          index: Number(s?.index),
          startFrameUrl: typeof s?.startFrameUrl === 'string' ? s.startFrameUrl : null,
        });
        const valid = (s: { index: number }) => Number.isFinite(s.index) && s.index >= 0;
        draftScenes = scenes.map(mapScene).filter(valid);
        pendingScenes = scenes
          .filter((s: Record<string, unknown>) => s?.clipStatus !== 'done')
          .map(mapScene)
          .filter(valid);
      }
    }
  } catch { /* non-fatal — copy just says "clips" without a total, no loading boxes */ }

  const [nomW, nomH] = aspectDims(aspect);
  const nomDisp = displayDims(nomW, nomH);
  // v5.61 — playbook card: a generation run shows the single-pass phase; a
  // Create Final Ad resume (nothing pending) shows only the finalize phase.
  const chat = createChatMirror(
    projectId,
    pendingScenes.length > 0 ? 'Generate the video' : 'Create the final ad',
    pendingScenes.length > 0 ? 'Generating the video ad…' : 'Finalizing…',
    {
      name: 'Clone Video Ad',
      phases: pendingScenes.length > 0
        ? [{ id: 'video_generation', name: 'Generate the full ad — one Seedance pass' }]
        : [{ id: 'finalize', name: 'Finalize the ad' }],
    },
  );
  // Fallback row (frames missing from this canvas — e.g. generate fired after
  // a reload into a different session): clips land on their own fresh row.
  const fallbackClipRowY = nextRowYUnderContent(projectId);
  let clipsDone = 0;
  let sawTerminal = false;

  // v5.13 — loading boxes: one per pending clip at the exact slot the clip
  // tile will land (one row beneath its frame when that frame is on this
  // canvas — the same lookup vc_clip_ready uses — else the fallback row), and
  // one for the final at the stitch phase. Run-scoped taskIds; no prompt (the
  // legacy retryPlaceholder path must never fire these into the image
  // pipeline). Placed BEFORE the SSE request so the run is visible on canvas
  // from the first moment.
  const phRun = `vcgen-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const clipPh = (sceneIndex: number) => `${phRun}-clip-${sceneIndex}`;
  const stitchPh = `${phRun}-stitch`;
  const outstanding = new Set<string>();
  const failOutstanding = (msg: string) => {
    // .forEach — the repo's TS target can't for..of a Set (TS2802).
    outstanding.forEach((tid) => failCanvasPlaceholder(tid, msg, projectId));
    outstanding.clear();
  };
  let stitchRowY: number | null = null;
  // v5.61 — single-row layout: the video lands to the RIGHT of the opening
  // frame (column 2) and the final to the right of the video (column 3).
  // Both slots derive from wherever the frame tile actually sits, so the run
  // stays one tidy row even if the user dragged the frame.
  let clipSlot: { x: number; y: number; w: number; h: number } | null = null;
  const finalSlot = () => (clipSlot ? { x: clipSlot.x + clipSlot.w + GEN_GAP, y: clipSlot.y } : null);
  {
    const imagesAtStart = useCanvasStore.getState().projects[projectId]?.images ?? [];
    // v5.58 — single-pass: ONE Seedance generation covers every scene, so
    // exactly ONE loading box, keyed by the first pending scene's index —
    // which is what vc_clip_ready will carry.
    const firstPending = pendingScenes[0];
    const anyFrameUrl = pendingScenes.find((s) => s.startFrameUrl)?.startFrameUrl
      ?? draftScenes.find((s) => s.startFrameUrl)?.startFrameUrl;
    // v6.90 — stem fallback: an exact-src miss (edit-chain drift) used to
    // drop the loading box to the fallback row, outside the run's box.
    const frameTile = (anyFrameUrl ? imagesAtStart.find((im) => im.src === anyFrameUrl) : undefined)
      ?? imagesAtStart.find((im) => im.src.includes(`/draft-${draftId}-scene-`) && !im.videoUrl && !/[?&]tile=/.test(im.src));
    if (frameTile) {
      // v7.14 — land at the END of this draft's row. Multi-window drafts have
      // 2-3 opening frames up there; anchoring on the first one dropped the
      // box on top of frame 2 and the collision guard shoved it mid-row.
      const rightEdge = draftRowRightEdge(imagesAtStart, draftId, frameTile.y, frameTile.displayHeight)
        ?? (frameTile.x + frameTile.displayWidth);
      clipSlot = {
        x: rightEdge + GEN_GAP,
        y: frameTile.y,
        w: frameTile.displayWidth,
        h: frameTile.displayHeight,
      };
    }
    // v6.06 — the LOADING BOX also lands beside the rightmost existing video
    // of this draft (v6.04 nudged only the finished tile; the box still used
    // the raw column-2 slot — Dan: "when i click generate in chat its
    // overlapping the video").
    if (clipSlot) {
      const band = clipSlot.h / 2;
      const priorVideos = imagesAtStart.filter((im) =>
        im.videoUrl && /[?&]tile=(clip-|final)/.test(im.src) &&
        videoCloneTileIndex.get(im.src)?.draftId === draftId &&
        Math.abs(im.y - clipSlot!.y) < band,
      );
      if (priorVideos.length > 0) {
        clipSlot.x = Math.max(clipSlot.x, Math.max(...priorVideos.map((im) => im.x + im.displayWidth)) + GEN_GAP);
      }
    }
    if (firstPending) {
      const x = clipSlot ? clipSlot.x : START_X + 2 * (nomDisp.w + GEN_GAP);
      const y = clipSlot ? clipSlot.y : fallbackClipRowY;
      const w = clipSlot ? clipSlot.w : nomDisp.w;
      const h = clipSlot ? clipSlot.h : nomDisp.h;
      if (!clipSlot) clipSlot = { x, y, w, h };
      addCanvasPlaceholder(
        clipPh(firstPending.index), x, y, w, h,
        undefined, nomW, nomH, 'video-ad.jpg', projectId,
      );
      outstanding.add(clipPh(firstPending.index));
    }
  }

  const invalidateDraft = () => queryClient.invalidateQueries({ queryKey: ['/api/video-clone', draftId] });

  try {
    await postSSE(
      `/api/video-clone/${draftId}/generate`,
      {},
      {
        vc_phase: (d) => {
          const phase = String(d.phase ?? '');
          const label = String(d.label ?? d.phase ?? 'Working…');
          if (phase === 'stitch') chat.startPhase('finalize', label);
          else chat.setLabel(label);
          // v5.61 — the final's loading box sits to the RIGHT of the video
          // (column 3 of the single row); fresh row only as a fallback.
          if (phase === 'stitch' && !outstanding.has(stitchPh)) {
            const slot = finalSlot();
            stitchRowY = slot ? slot.y : nextRowYUnderContent(projectId);
            addCanvasPlaceholder(
              stitchPh, slot ? slot.x : START_X, stitchRowY, clipSlot?.w ?? nomDisp.w, clipSlot?.h ?? nomDisp.h,
              undefined, nomW, nomH, 'final-video-ad.jpg', projectId,
            );
            outstanding.add(stitchPh);
          }
        },
        vc_clip_progress: (d) => {
          const attempt = Number(d.attempt ?? 0);
          chat.setLabel(`${String(d.label ?? 'Generating clip…')}${attempt > 0 ? ' (retry)' : ''}`);
        },
        vc_clip_ready: async (d) => {
          const sceneIndex = Number(d.sceneIndex ?? 0);
          const videoUrl = String(d.videoUrl ?? '');
          const posterUrl = String(d.posterUrl ?? '');
          if (!videoUrl || !posterUrl) return;
          const tid = clipPh(sceneIndex);
          if (outstanding.has(tid)) {
            resolveCanvasPlaceholder(tid, projectId);
            outstanding.delete(tid);
          }
          // v5.61 — the video lands at COLUMN 2: to the RIGHT of the opening
          // frame (posterUrl is that frame). Fallback row only when the frame
          // isn't on this canvas.
          const images = useCanvasStore.getState().projects[projectId]?.images ?? [];
          // v6.90 — stem fallback (see materializeCloneTiles).
          const frameTile = images.find((im) => im.src === posterUrl)
            ?? images.find((im) => im.src.includes(`/draft-${draftId}-scene-`) && !im.videoUrl && !/[?&]tile=/.test(im.src));
          if (frameTile) {
            // v7.14 — end of the row (multi-window drafts have 2-3 frames).
            const rightEdge = draftRowRightEdge(images, draftId, frameTile.y, frameTile.displayHeight)
              ?? (frameTile.x + frameTile.displayWidth);
            clipSlot = {
              x: rightEdge + GEN_GAP,
              y: frameTile.y,
              w: frameTile.displayWidth,
              h: frameTile.displayHeight,
            };
          }
          let x = clipSlot ? clipSlot.x : START_X + 2 * (nomDisp.w + GEN_GAP);
          const y = clipSlot ? clipSlot.y : fallbackClipRowY;
          const w = frameTile?.originalWidth || nomW;
          const h = frameTile?.originalHeight || nomH;
          // v6.04 — a re-generate KEEPS every previous video and lands
          // BESIDE the rightmost one in this row (Dan: "it just made the
          // video over TOP of the old one — it should put it beside it").
          const rowBand = (frameTile?.displayHeight ?? nomDisp.h) / 2;
          const priorVideos = images.filter((im) =>
            im.videoUrl &&
            /[?&]tile=(clip-|final)/.test(im.src) &&
            videoCloneTileIndex.get(im.src)?.draftId === draftId &&
            Math.abs(im.y - y) < rowBand,
          );
          if (priorVideos.length > 0) {
            const rightmost = Math.max(...priorVideos.map((im) => im.x + im.displayWidth));
            x = Math.max(x, rightmost + GEN_GAP);
          }
          // v5.58 — single-pass: the ONE video covers every scene.
          const whole = d.wholeVideo === true;
          // v6.96 — multi-window clips are labeled by PART, not scene.
          const part = Number(d.part ?? 0);
          const parts = Number(d.parts ?? 0);
          // v6.04/v6.11 — generation-suffixed tag: a re-generate with the
          // SAME poster is still a NEW tile, and the suffix is derived from
          // the videoUrl so it matches the server's persist-first row.
          const src = withTileTag(posterUrl, videoTileGenTag(`clip-${sceneIndex}`, videoUrl));
          videoCloneTileIndex.set(src, { draftId, sceneIndex });
          await addImageToCanvas(
            src, w, h, whole ? 'video-ad.jpg' : part > 0 ? `video-part-${part}.jpg` : `scene-${sceneIndex + 1}-clip.jpg`,
            w, h, whole ? 'Video ad — all scenes, one pass' : part > 0 ? `Video part ${part}${parts > 0 ? ` of ${parts}` : ''}` : `Scene ${sceneIndex + 1} video clip`,
            x, y, { videoUrl, conceptName: whole ? 'Video ad' : part > 0 ? `Video part ${part}` : `Scene ${sceneIndex + 1} clip`, workflowRunId: runWorkflowRunId ?? undefined }, projectId,
          );
          clipsDone++;
          chat.addThumbnail(posterUrl);
          // v5.23 — QA verdict rides vc_clip_ready additively; a flagged
          // video still ships (the retry already burned), but the user should
          // know before finalizing.
          if (d.qaPassed === false) {
            chat.addWarning(whole
              ? `Video flagged by QA: ${String(d.qaReason ?? 'products disappeared mid-video')} — regenerate from the tile if it shows.`
              : `Scene ${sceneIndex + 1} clip flagged by QA: ${String(d.qaReason ?? 'products disappeared mid-clip')} — regenerate the frame + clip if it shows.`);
          }
          chat.setLabel(whole
            ? 'Video ad generated — reviewing…'
            : part > 0
              ? `Video part ${part}${parts > 0 ? ` of ${parts}` : ''} done…`
              : sceneCount > 0
                ? `Scene ${sceneIndex + 1} clip done (${clipsDone}/${sceneCount})…`
                : `Scene ${sceneIndex + 1} clip done…`);
          invalidateDraft();
        },
        vc_clip_failed: (d) => {
          const sceneIndex = Number(d.sceneIndex ?? 0);
          const whole = d.wholeVideo === true;
          const tid = clipPh(sceneIndex);
          if (outstanding.has(tid)) {
            failCanvasPlaceholder(tid, whole ? 'Video failed — Generate again retries it.' : 'Clip failed — Generate again retries it.', projectId);
            outstanding.delete(tid);
          }
          // v6.03 — surface the PROVIDER's reason: "submit_error" hid
          // "Insufficient credits. Please top up your account" behind a
          // useless retry suggestion (Dan retried an unretryable state).
          const reason = typeof d.error === 'string' && d.error.trim() ? ` — ${d.error.trim().slice(0, 160)}` : '';
          const isProviderCredits = /insufficient credits|top up/i.test(String(d.error ?? ''));
          chat.addWarning(whole
            ? `Video generation failed (${String(d.kind ?? 'error')})${reason}${isProviderCredits ? ' [WaveSpeed account balance — top up at wavespeed.ai, then Generate again]' : ' — hit Generate again to retry.'}`
            : `Scene ${sceneIndex + 1} clip failed (${String(d.kind ?? 'error')})${reason} — Generate again retries just the failed scenes.`);
          invalidateDraft();
        },
        vc_clips_done: (d) => {
          sawTerminal = true;
          // v5.38 — clip runs STOP before the stitch so every clip (audio
          // included) can be reviewed. Resolve leftovers like vc_final_ready:
          // clips are persist-first server-side, a leftover box is a spinner
          // over a clip that exists.
          outstanding.forEach((tid) => resolveCanvasPlaceholder(tid, projectId));
          outstanding.clear();
          invalidateDraft();
          if (d.wholeVideo === true) {
            chat.finish(
              'Your video ad is ready — hover the tile to review the video AND audio. Regenerate it from the tile if needed, then hit Create Final Ad in the script doc to finalize.',
            );
            return;
          }
          const n = Number(d.sceneCount ?? sceneCount) || sceneCount;
          chat.finish(
            `All ${n > 0 ? n + ' ' : ''}clips are ready — hover each one to review the video AND audio. Regenerate any clip from its tile's regenerate button, then hit Create Final Ad in the script doc to stitch the final cut.`,
          );
        },
        vc_final_ready: async (d) => {
          sawTerminal = true;
          // Resolve (never fail) EVERYTHING still outstanding — the stitch box
          // plus any clip box whose vc_clip_ready was dropped mid-stream. The
          // final only stitches when every clip succeeded, and the server
          // persists clip tiles BEFORE emitting (persist-first), so a leftover
          // box here is a spinner over a clip that exists in the DB — failing
          // it red (or leaving it) would misreport a finished run.
          outstanding.forEach((tid) => resolveCanvasPlaceholder(tid, projectId));
          outstanding.clear();
          const videoUrl = String(d.videoUrl ?? '');
          const posterUrl = String(d.posterUrl ?? '');
          if (videoUrl && posterUrl) {
            // v5.61 — COLUMN 3 of the single row (right of the video); the
            // stitch box's slot when one was placed, else a fresh row.
            const slot = finalSlot();
            let x = slot ? slot.x : START_X;
            const y = slot ? slot.y : (stitchRowY ?? nextRowYUnderContent(projectId));
            // v6.04 — same keep-and-place-beside rule as clip tiles.
            const imgsNow = useCanvasStore.getState().projects[projectId]?.images ?? [];
            const priorFinals = imgsNow.filter((im) =>
              im.videoUrl && /[?&]tile=final/.test(im.src) &&
              videoCloneTileIndex.get(im.src)?.draftId === draftId &&
              Math.abs(im.y - y) < (im.displayHeight || 1) / 2,
            );
            if (priorFinals.length > 0) {
              x = Math.max(x, Math.max(...priorFinals.map((im) => im.x + im.displayWidth)) + GEN_GAP);
            }
            const src = withTileTag(posterUrl, videoTileGenTag('final', videoUrl));
            videoCloneTileIndex.set(src, { draftId, sceneIndex: null });
            await addImageToCanvas(
              src, nomW, nomH, 'final-video-ad.jpg',
              nomW, nomH, 'Final video ad',
              x, y, { videoUrl, conceptName: 'Final video ad', workflowRunId: runWorkflowRunId ?? undefined }, projectId,
            );
            chat.addThumbnail(posterUrl);
          }
          invalidateDraft();
          chat.finish('Done — the final video ad is on the canvas.');
        },
        vc_error: (d) => {
          sawTerminal = true;
          // Covers a stitch failure (the final's box) AND any clip whose
          // vc_clip_* event never arrived.
          failOutstanding('Video generation failed');
          invalidateDraft();
          chat.fail(`Video generation failed: ${String(d.error ?? 'unknown error')}`);
        },
      },
      (message) => {
        sawTerminal = true;
        // Pre-SSE gate rejection (402 credits / 409 status / 422 validation):
        // the run never started, so REMOVE the just-placed clip boxes instead
        // of painting them as red generation failures — the chat line carries
        // the rejection.
        outstanding.forEach((tid) => resolveCanvasPlaceholder(tid, projectId));
        outstanding.clear();
        chat.fail(message);
      },
    );
    if (!sawTerminal) {
      failOutstanding('Connection lost — it may still be running. Reopen the script doc.');
      invalidateDraft();
      chat.fail('Connection lost mid-run — the generation may still be running. Reopen the script doc to check.');
    }
  } catch (err) {
    console.error('[VideoCloneRunner] generate error:', err);
    failOutstanding('Video generation failed');
    invalidateDraft();
    if (!sawTerminal) chat.fail('Video generation failed — see console for details.');
    throw err;
  }
}

// ─── VIDEO TILE REGENERATION (v5.38) ─────────────────────────────────────────
// The video tiles' hover Regenerate button. Clip tiles: mark that ONE clip
// stale (free) then run generate — it regenerates exactly the stale clip and,
// per the review-then-stitch flow, stops before the stitch. Final tile: flip
// the completed draft back to ready (free) then run generate — all clips are
// done, so the run takes the stitch-only free-resume path and re-stitches.

export async function regenerateVideoCloneClip(
  draftId: number,
  sceneIndex: number,
  projectId: number,
): Promise<void> {
  const res = await fetch(`/api/video-clone/${draftId}/scenes/${sceneIndex}/reset-clip`, { method: 'POST' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    toast({
      title: 'Could not regenerate clip',
      description: String((body as { error?: string }).error ?? `HTTP ${res.status}`),
      variant: 'destructive',
    });
    return;
  }
  await runVideoCloneGenerate(draftId, projectId);
}

/** v5.71 — frame regen WITH canvas mirroring (Dan: the doc-only regen left
 *  the canvas untouched — no grey loading box, no new tile, and the old or
 *  failed frame box sat there forever). Places a loading box at the frame's
 *  slot, runs the regen endpoint, then swaps the old frame tile for the new
 *  one. Throws Error & {status?, body?} so the doc keeps its 402 handling. */
export async function regenerateVideoCloneFrame(
  draftId: number,
  sceneIndex: number,
  projectId: number,
  aspect: string,
): Promise<{ url: string; qaPassed: boolean | null; qaReason: string | null }> {
  const { addImageToCanvas, addCanvasPlaceholder, resolveCanvasPlaceholder, failCanvasPlaceholder, deleteImages } = useCanvasStore.getState();
  const [nomW, nomH] = aspectDims(aspect);
  const disp = displayDims(nomW, nomH);
  const images = useCanvasStore.getState().projects[projectId]?.images ?? [];
  // The draft's run id anchors both the slot and the section grouping —
  // needed when NO frame tile exists yet (failed prepare: Dan's regen box
  // rendered at the bottom of the canvas instead of beside the source video).
  let runId: number | undefined;
  let priorFrameUrl: string | null = null;
  try {
    const dres = await fetch(`/api/video-clone/${draftId}`);
    if (dres.ok) {
      const d = await dres.json();
      const rid = Number(d?.workflowRunId);
      if (Number.isFinite(rid) && rid > 0) runId = rid;
      const sc = (d?.script?.scenes ?? []).find((s: { index?: number }) => Number(s?.index) === sceneIndex);
      priorFrameUrl = typeof sc?.startFrameUrl === 'string' ? sc.startFrameUrl : null;
    }
  } catch { /* non-fatal — slot falls back */ }
  // Slot priority: the current frame tile → right of the section's source
  // video tile → right of any tile in the run → fresh row (last resort).
  const oldTile = images.find((im) => {
    const link = videoCloneTileIndex.get(im.src);
    return link?.draftId === draftId && link.sceneIndex === sceneIndex && !im.videoUrl && isVideoCloneFrameSrc(im.src);
  });
  const runImages = runId != null ? images.filter((im) => (im as unknown as { workflowRunId?: number }).workflowRunId === runId) : [];
  const sourceTile = runImages.find((im) => im.src.includes('tile=source-video'));
  const leftmostRun = runImages.slice().sort((a, b) => a.x - b.x)[0];
  const anchor = oldTile ?? null;
  const beside = sourceTile ?? leftmostRun ?? null;
  const x = anchor ? anchor.x : beside ? beside.x + beside.displayWidth + GEN_GAP : START_X + (disp.w + GEN_GAP);
  const y = anchor ? anchor.y : beside ? beside.y : nextRowYUnderContent(projectId);
  const w = anchor?.displayWidth ?? beside?.displayWidth ?? disp.w;
  const h = anchor?.displayHeight ?? beside?.displayHeight ?? disp.h;
  const tid = `vcframe-regen-${draftId}-${sceneIndex}-${Date.now()}`;
  addCanvasPlaceholder(tid, x, y, w, h, undefined, nomW, nomH, 'opening-frame.jpg', projectId);
  try {
    const res = await fetch(`/api/video-clone/${draftId}/scenes/${sceneIndex}/regen-frame`, { method: 'POST' });
    if (!res.ok) {
      let body: Record<string, unknown> | null = null;
      try { body = await res.json(); } catch { /* non-JSON */ }
      const err = new Error(body?.error ? String(body.error) : `HTTP ${res.status}`) as Error & { status?: number; body?: Record<string, unknown> | null };
      err.status = res.status;
      err.body = body;
      throw err;
    }
    // v5.73 — the route answers 202 immediately (Cloudflare kills long plain
    // requests at ~100s; the old await surfaced errors over SUCCESSFUL
    // regens). Poll the draft until it leaves "preparing", then judge by the
    // frame URL: changed = success, unchanged = the regen failed server-side.
    let data: { url: string; qaPassed: boolean | null; qaReason: string | null } | null = null;
    for (let i = 0; i < 150; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      let d: { status?: string; script?: { scenes?: Array<{ index?: number; startFrameUrl?: string | null }> } } | null = null;
      try {
        const pres = await fetch(`/api/video-clone/${draftId}`);
        if (!pres.ok) continue;
        d = await pres.json();
      } catch { continue; }
      if (d?.status === 'preparing') continue;
      const sc = (d?.script?.scenes ?? []).find((s) => Number(s?.index) === sceneIndex);
      const url = typeof sc?.startFrameUrl === 'string' ? sc.startFrameUrl : null;
      if (url && url !== priorFrameUrl) {
        data = { url, qaPassed: null, qaReason: null };
      }
      break;
    }
    if (!data) {
      const err = new Error('Frame regeneration failed — check the scene in the script doc and try again.') as Error & { status?: number };
      err.status = 502;
      throw err;
    }
    resolveCanvasPlaceholder(tid, projectId);
    if (oldTile) deleteImages([oldTile.id]);
    videoCloneTileIndex.set(data.url, { draftId, sceneIndex });
    await addImageToCanvas(
      data.url, nomW, nomH, 'opening-frame.jpg',
      nomW, nomH, 'Opening frame — anchors the whole video',
      x, y,
      // workflowRunId is a TOP-LEVEL image field (v5.71 read it from a
      // non-existent adMeta nest — the new tile fell outside the section).
      { workflowRunId: runId ?? (oldTile as unknown as { workflowRunId?: number })?.workflowRunId ?? undefined },
      projectId,
    );
    return data;
  } catch (e) {
    failCanvasPlaceholder(tid, 'Frame regen failed — try again from the script doc.', projectId);
    throw e;
  }
}

export async function restitchVideoCloneFinal(draftId: number, projectId: number): Promise<void> {
  const res = await fetch(`/api/video-clone/${draftId}/reset-final`, { method: 'POST' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    toast({
      title: 'Could not re-stitch the final video',
      description: String((body as { error?: string }).error ?? `HTTP ${res.status}`),
      variant: 'destructive',
    });
    return;
  }
  await runVideoCloneGenerate(draftId, projectId);
}
